import { Utils } from '@genial/utils';

import { PublicOfferApplicationProps, PublicOfferUserSelectionValues } from '../..';
import { PublicOfferTypes } from '../../../../../../../../../../store/product/external/constants';
import { ReturnTypeOf_RealizarPedidoReserva } from '../../../../../../../../../../store/product/external/types';
import { PublicOfferAssetToInvestNormalized } from '../../../../../../../../../../store/product/types';
import { ProductCardItemProps } from '../../../../components/ProductCardItem';

const formatToMoneyIfExist = (value?: number) => {
  if (value) {
    return Utils.Mask.Money(value);
  }
  return '';
};

const formatToPercentageIfExist = (value?: number) => {
  if (value) {
    return Utils.Mask.Percentage(value);
  }
  return '';
};

const showInfoIfExists = (info: ProductCardItemProps) => {
  if (info.value) {
    return [info];
  }
  return [];
};

export const getInfos = (
  offer: PublicOfferApplicationProps['offer'],
  userSelectionValues: PublicOfferUserSelectionValues,
  publicOfferAssetDetails?: PublicOfferAssetToInvestNormalized
) => {
  const liquidationInfo = {
    label: 'Liquidação',
    value: offer.liquidityDateFormatted,
  } as ProductCardItemProps;

  const quantityInfo = {
    label: 'Quantidade',
    value: userSelectionValues.amountItem?.toString(),
  } as ProductCardItemProps;

  const unitaryPriceInfo = {
    label: 'Preço Unitário',
    value: publicOfferAssetDetails?.unitaryPriceFormatted,
    loading: !publicOfferAssetDetails,
  } as ProductCardItemProps;

  const valueInfo = {
    label: 'Valor investido',
    value: formatToMoneyIfExist(userSelectionValues.currentValue),
  } as ProductCardItemProps;

  const totalValueInfo = {
    label: 'Valor total',
    value: formatToMoneyIfExist(userSelectionValues.currentValue),
  } as ProductCardItemProps;

  const equityValueInfo = {
    label: 'Valor máximo por ação',
    value: userSelectionValues.useMarketValueEquity
      ? 'A mercado'
      : formatToMoneyIfExist(userSelectionValues.currentEquityValue),
  } as ProductCardItemProps;

  const minimumRemunerationInfo = {
    label: 'Remuneração mínima',
    value: userSelectionValues.useMarketValueRemuneration
      ? 'A mercado'
      : formatToPercentageIfExist(userSelectionValues.currentRemuneration),
  };

  return {
    liquidationInfo,
    quantityInfo,
    unitaryPriceInfo,
    valueInfo,
    totalValueInfo,
    equityValueInfo,
    minimumRemunerationInfo,
  };
};

export const getProductConfirmationInfos = (
  offer: PublicOfferApplicationProps['offer'],
  userSelectionValues: PublicOfferUserSelectionValues,
  publicOfferAssetDetails?: PublicOfferAssetToInvestNormalized
): ProductCardItemProps[] => {
  const isQuantityProduct =
    offer.limiterCode === 2 || offer.publicOfferType === PublicOfferTypes.COE_FIXED;

  const {
    liquidationInfo,
    quantityInfo,
    unitaryPriceInfo,
    valueInfo,
    totalValueInfo,
    equityValueInfo,
    minimumRemunerationInfo,
  } = getInfos(offer, userSelectionValues, publicOfferAssetDetails);

  if (isQuantityProduct) {
    return [liquidationInfo, quantityInfo, unitaryPriceInfo, valueInfo, totalValueInfo];
  }

  return [
    liquidationInfo,
    ...showInfoIfExists(equityValueInfo),
    ...showInfoIfExists(minimumRemunerationInfo),
    valueInfo,
    totalValueInfo,
  ];
};

export const getProductSuccessInfos = (
  offer: PublicOfferApplicationProps['offer'],
  userSelectionValues: PublicOfferUserSelectionValues,
  responseData: ReturnTypeOf_RealizarPedidoReserva,
  publicOfferAssetDetails?: PublicOfferAssetToInvestNormalized
): ProductCardItemProps[] => {
  const isQuantityProduct =
    offer.limiterCode === 2 || offer.publicOfferType === PublicOfferTypes.COE_FIXED;

  const protocolInfo = {
    label: 'Protocolo',
    value: responseData?.identificador,
  } as ProductCardItemProps;

  const operationInfo = {
    label: 'Operação',
    value: offer.offerName,
  } as ProductCardItemProps;

  const {
    liquidationInfo,
    quantityInfo,
    unitaryPriceInfo,
    totalValueInfo,
    equityValueInfo,
    minimumRemunerationInfo,
  } = getInfos(offer, userSelectionValues, publicOfferAssetDetails);

  if (isQuantityProduct) {
    return [protocolInfo, operationInfo, quantityInfo, unitaryPriceInfo, totalValueInfo];
  }

  return [
    protocolInfo,
    operationInfo,
    liquidationInfo,
    ...showInfoIfExists(equityValueInfo),
    ...showInfoIfExists(minimumRemunerationInfo),
    totalValueInfo,
  ];
};

export const getMaximumRate = (
  offer: PublicOfferApplicationProps['offer'],
  userSelectionValues: PublicOfferUserSelectionValues
) => {
  const {
    currentRemuneration,
    currentEquityValue,
    useMarketValueEquity,
    useMarketValueRemuneration,
  } = userSelectionValues;

  if (offer.limiterCode === 2 || offer.publicOfferType === PublicOfferTypes.COE_FIXED) {
    return offer.maximumRemunerationAsReceived;
  }
  if (offer.limiterCode === 0) {
    return useMarketValueEquity ? undefined : currentEquityValue;
  }

  return useMarketValueRemuneration ? undefined : currentRemuneration;
};
